import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, X, Sparkles, Rocket } from 'lucide-react';

const TOUR_KEY = 'tcg_gacha_tour_completed';

const HOME_STEPS = [
  {
    target: '[data-testid="home-ticket-balance"]',
    title: 'Tickets',
    content: 'Your Tickets — earned from gacha pulls. Redeem them for exclusive items in the Shop!',
    position: 'bottom',
    icon: '🎟️',
  },
  {
    target: '[data-testid="home-coin-balance"]',
    title: 'Coin Balance',
    content: 'Coins are your main currency. Use them to pull gacha cards! Tap + to deposit SOL and get more.',
    position: 'bottom',
    icon: '🪙',
  },
  {
    target: 'input[placeholder*="Search"]',
    title: 'Search',
    content: 'Quickly find any pool or collection by name. Type and discover!',
    position: 'bottom',
    icon: '🔍',
  },
  {
    target: '[data-testid="home-categories-section"]',
    title: 'Categories',
    content: 'Browse card pools organized by category. Tap a category to explore its themed collections.',
    position: 'bottom',
    icon: '📂',
  },
  {
    target: '[data-testid="home-first-pool-list"]',
    title: 'Pool Cards',
    content: 'Each card pool has unique items with different rarities. Tap a card to view details and start pulling gacha!',
    position: 'bottom',
    icon: '🃏',
  },
  {
    target: '[data-testid="nav-pools"]',
    title: 'Pools',
    content: 'All gacha pools live here. Each pool has unique cards from Rare to THE ONE AND ONLY!',
    position: 'top',
    icon: '🎰',
  },
  {
    target: '[data-testid="nav-collection"]',
    title: 'Collection',
    content: 'Cards you\'ve won are stored here. Sell them for coins or mint as NFTs!',
    position: 'top',
    icon: '🃏',
  },
  {
    target: '[data-testid="nav-profile"]',
    title: 'Profile',
    content: 'Your account hub — wallet, stats, and settings. Let\'s check it out!',
    position: 'top',
    icon: '👤',
    isLast: true,
  },
];

const PROFILE_STEPS = [
  {
    target: '[data-testid="profile-user-info-card"]',
    title: 'Your Info',
    content: 'Account details, VIP level, and status at a glance. Level up by staking more!',
    position: 'bottom',
    icon: '📋',
  },
  {
    target: '[data-testid="profile-balance-section"]',
    title: 'Balance & Tickets',
    content: 'Track your Coins for gacha and Tickets for shop redemptions — all in one place.',
    position: 'bottom',
    icon: '💰',
  },
  {
    target: '[data-testid="profile-wallet-info"]',
    title: 'Wallet Info',
    content: 'Your Solana wallet — SOL balance and Token rewards. Deposit SOL to get coins with cashback!',
    position: 'bottom',
    icon: '💎',
    isLast: true,
  },
];

const TOOLTIP_GAP = 22;
const TOOLTIP_BG = '#0c2038';
const OWL_VIDEO = '/owl-guide.webm';

const TourTooltip = ({ step, stepNum, totalSteps, onNext, onPrev, onSkip, targetRect, phase }) => {
  const isTop = step.position === 'top';
  const tooltipW = 340;
  const tooltipH = 170;

  let top, left;
  if (isTop) {
    top = targetRect.top - tooltipH - TOOLTIP_GAP;
  } else {
    top = targetRect.bottom + TOOLTIP_GAP;
  }
  left = targetRect.left + targetRect.width / 2 - tooltipW / 2;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 430;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 932;
  if (left < 12) left = 12;
  if (left + tooltipW > vw - 12) left = vw - 12 - tooltipW;
  if (top < 12) top = 12;
  if (top + tooltipH > vh - 12) top = vh - 12 - tooltipH;

  const progress = ((stepNum + 1) / totalSteps) * 100;

  return (
    <div
      className="fixed z-[10002]"
      style={{ top: `${top}px`, left: `${left}px`, width: 340 }}
    >
      <motion.div
        initial={{ opacity: 0, y: isTop ? 10 : -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: isTop ? 10 : -10, scale: 0.95 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
      {/* Arrow */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 ${
          isTop
            ? `bottom-[-8px] border-l-[9px] border-r-[9px] border-t-[9px] border-l-transparent border-r-transparent`
            : `top-[-8px] border-l-[9px] border-r-[9px] border-b-[9px] border-l-transparent border-r-transparent`
        }`}
        style={{
          [isTop ? 'borderTopColor' : 'borderBottomColor']: TOOLTIP_BG,
          filter: 'drop-shadow(0 1px 3px rgba(6,182,212,0.25))',
        }}
      />

      <div
        className="rounded-2xl overflow-hidden shadow-2xl shadow-amber-950/40 border border-amber-700/30"
        style={{ backgroundColor: TOOLTIP_BG }}
      >
        {/* Progress bar */}
        <div className="h-[3px] bg-amber-950/60">
          <motion.div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-400"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">{step.icon}</span>
              <h3 className="text-amber-50 font-bold text-sm" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
                {step.title}
              </h3>
            </div>
            <button
              onClick={onSkip}
              className="text-amber-800 hover:text-amber-400 transition-colors p-0.5"
              aria-label="Skip tour"
              data-testid="tour-skip-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body with Owl Video */}
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-shrink-0 w-[56px] h-[56px] rounded-xl overflow-hidden">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
                style={{ mixBlendMode: 'screen' }}
              >
                <source src={OWL_VIDEO} type="video/webm" />
              </video>
            </div>
            <p className="text-amber-200/70 text-xs leading-relaxed flex-1">
              {step.content}
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <span className="text-amber-700 text-[10px] font-medium tracking-wide">
              {stepNum + 1} / {totalSteps}
            </span>

            <div className="flex items-center gap-2">
              {stepNum > 0 && (
                <button
                  onClick={onPrev}
                  className="flex items-center gap-1 text-amber-500/70 hover:text-amber-300 text-xs transition-colors px-2 py-1 rounded-lg hover:bg-amber-900/30"
                  data-testid="tour-back-btn"
                >
                  <ChevronLeft className="w-3 h-3" />
                  Back
                </button>
              )}
              <button
                onClick={onNext}
                className="flex items-center gap-1 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white text-xs font-semibold px-4 py-1.5 rounded-lg transition-all shadow-lg shadow-amber-900/40"
                data-testid="tour-next-btn"
              >
                {step.isLast ? (
                  <>
                    <Sparkles className="w-3 h-3" />
                    {phase === 'home' ? "Let's Go!" : 'Finish'}
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-3 h-3" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      </motion.div>
    </div>
  );
};

const SpotlightOverlay = ({ targetRect, onClick }) => {
  if (!targetRect) return null;
  const pad = 8;
  const r = 14;
  const x = targetRect.left - pad;
  const y = targetRect.top - pad;
  const w = targetRect.width + pad * 2;
  const h = targetRect.height + pad * 2;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[10000]"
      onClick={onClick}
    >
      <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="black" />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.72)" mask="url(#tour-mask)" />
        {/* Glow ring */}
        <rect
          x={x - 1.5} y={y - 1.5} width={w + 3} height={h + 3}
          rx={r + 2} ry={r + 2}
          fill="none" stroke="rgba(6,182,212,0.4)" strokeWidth="2"
        />
        <rect
          x={x - 4} y={y - 4} width={w + 8} height={h + 8}
          rx={r + 4} ry={r + 4}
          fill="none" stroke="rgba(6,182,212,0.12)" strokeWidth="3"
        />
      </svg>
    </motion.div>
  );
};

const WelcomeSplash = ({ onStart, onSkip }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/80 backdrop-blur-sm"
  >
    <motion.div
      initial={{ scale: 0.85, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.85, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      className="rounded-2xl p-6 mx-6 max-w-[340px] w-full text-center shadow-2xl shadow-amber-950/50 border border-amber-700/30"
      style={{ backgroundColor: TOOLTIP_BG }}
    >
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-lg shadow-amber-600/30">
        <Sparkles className="w-7 h-7 text-white" />
      </div>
      <h2 className="text-amber-50 text-lg font-bold mb-2" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
        Welcome to Cardtopia!
      </h2>
      <p className="text-amber-200/60 text-sm leading-relaxed mb-6">
        Let us show you around — a quick tour of the key features to get you started.
      </p>
      <div className="flex flex-col gap-2">
        <button
          onClick={onStart}
          className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-semibold py-2.5 px-4 rounded-xl text-sm transition-all shadow-lg shadow-amber-900/40"
          data-testid="tour-start-btn"
        >
          Start Tour
        </button>
        <button
          onClick={onSkip}
          className="w-full text-amber-600/70 hover:text-amber-300 text-xs py-2 transition-colors"
          data-testid="tour-skip-intro-btn"
        >
          Skip, I'll explore on my own
        </button>
      </div>
    </motion.div>
  </motion.div>
);

// Confetti particles on canvas
const ConfettiCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#FF8C00', '#FFC300', '#fbbf24', '#f59e0b', '#a78bfa', '#f472b6', '#34d399', '#fb923c'];
    const particles = [];

    for (let i = 0; i < 150; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * canvas.height,
        w: 4 + Math.random() * 6,
        h: 8 + Math.random() * 10,
        color: colors[Math.floor(Math.random() * colors.length)],
        vy: 2 + Math.random() * 4,
        vx: (Math.random() - 0.5) * 3,
        rot: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 8,
        opacity: 0.7 + Math.random() * 0.3,
      });
    }

    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.y += p.vy;
        p.x += p.vx;
        p.rot += p.rotSpeed;
        if (p.y > canvas.height + 20) {
          p.y = -20;
          p.x = Math.random() * canvas.width;
        }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rot * Math.PI) / 180);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />;
};

// Celebration screen after tour finishes
const CelebrationScreen = ({ onDone }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/85 backdrop-blur-sm"
  >
    <ConfettiCanvas />
    <motion.div
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.5, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 18, delay: 0.2 }}
      className="relative z-10 flex flex-col items-center mx-6 max-w-[360px] w-full text-center"
    >
      {/* Owl video with glow */}
      <div className="relative mb-4">
        <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-3xl scale-150" />
        <div className="relative w-32 h-32 rounded-full overflow-hidden">
          <video
            autoPlay loop muted playsInline
            className="w-full h-full object-cover scale-[1.3]"
            style={{ mixBlendMode: 'screen' }}
          >
            <source src={OWL_VIDEO} type="video/webm" />
          </video>
        </div>
      </div>

      {/* Congrats text */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "'Chakra Petch', sans-serif" }}>
          Congratulations!
        </h2>
        <p className="text-amber-300 text-base font-medium mb-1">
          Your Adventure Begins Now
        </p>
        <p className="text-amber-200/50 text-xs mb-8">
          You're all set to explore, collect, and conquer. Good luck!
        </p>
      </motion.div>

      {/* CTA Button */}
      <motion.button
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onDone}
        className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-bold py-3 px-8 rounded-2xl text-sm shadow-xl shadow-amber-600/30 transition-all"
        data-testid="tour-celebrate-btn"
      >
        <Rocket className="w-4 h-4" />
        Start Exploring!
      </motion.button>
    </motion.div>
  </motion.div>
);

const OnboardingTour = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [phase, setPhase] = useState('idle');
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem(TOUR_KEY)) return;
    if (location.pathname !== '/home') return;
    if (phase !== 'idle') return;
    const timer = setTimeout(() => setPhase('welcome'), 1500);
    return () => clearTimeout(timer);
  }, [user, location.pathname, phase]);

  useEffect(() => {
    if (phase === 'home') {
      const available = HOME_STEPS.filter(s => document.querySelector(s.target));
      const tokenEl = document.querySelector('[data-testid="home-token-balance"]');
      if (tokenEl) {
        const ticketIdx = available.findIndex(s => s.target.includes('ticket'));
        available.splice(ticketIdx >= 0 ? ticketIdx + 1 : 1, 0, {
          target: '[data-testid="home-token-balance"]',
          title: 'Token Balance',
          content: 'On-chain Token balance from your Solana wallet. Earn tokens through cashback on deposits!',
          position: 'bottom',
          icon: '✨',
        });
      }
      if (available.length > 0) available[available.length - 1].isLast = true;
      setSteps(available);
      setCurrentStep(0);
    }
    if (phase === 'profile') {
      const timer = setTimeout(() => {
        const available = PROFILE_STEPS.filter(s => document.querySelector(s.target));
        if (available.length > 0) available[available.length - 1].isLast = true;
        setSteps(available);
        setCurrentStep(0);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [phase]);

  useEffect(() => {
    if (phase === 'nav-to-profile') {
      navigate('/profile');
      const timer = setTimeout(() => setPhase('profile'), 300);
      return () => clearTimeout(timer);
    }
  }, [phase, navigate]);

  useEffect(() => {
    if ((phase !== 'home' && phase !== 'profile') || steps.length === 0) {
      setTargetRect(null);
      return;
    }
    const step = steps[currentStep];
    if (!step) return;

    const updateRect = () => {
      const el = document.querySelector(step.target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          const rect = el.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height, bottom: rect.bottom, right: rect.right });
          }
        }, 350);
      }
    };
    updateRect();

    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [phase, currentStep, steps]);

  const finishTour = useCallback(() => {
    localStorage.setItem(TOUR_KEY, 'true');
    setTargetRect(null);
    navigate('/home');
    setPhase('celebration');
  }, [navigate]);

  const dismissCelebration = useCallback(() => {
    setPhase('done');
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else if (phase === 'home') {
      setTargetRect(null);
      setPhase('nav-to-profile');
    } else {
      finishTour();
    }
  }, [currentStep, steps.length, phase, finishTour]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  }, [currentStep]);

  if (phase === 'idle' || phase === 'done' || phase === 'nav-to-profile') return null;

  return createPortal(
    <AnimatePresence mode="wait">
      {phase === 'welcome' && (
        <WelcomeSplash
          key="welcome"
          onStart={() => setPhase('home')}
          onSkip={finishTour}
        />
      )}
      {phase === 'celebration' && (
        <CelebrationScreen key="celebration" onDone={dismissCelebration} />
      )}
      {(phase === 'home' || phase === 'profile') && steps.length > 0 && steps[currentStep] && (
        <React.Fragment key={`tour-${phase}-${currentStep}`}>
          <SpotlightOverlay targetRect={targetRect} onClick={() => {}} />
          {targetRect && (
            <TourTooltip
              step={steps[currentStep]}
              stepNum={currentStep}
              totalSteps={steps.length}
              onNext={handleNext}
              onPrev={handlePrev}
              onSkip={finishTour}
              targetRect={targetRect}
              phase={phase}
            />
          )}
        </React.Fragment>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default OnboardingTour;
